import type { CSSProperties } from 'react';
import { CarouselItem } from '@rocco/components/carousel';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';

interface BrowseMoreTileProps {
    /** Listing destination, e.g. "/projects". */
    to: string;
    /** Listing noun, e.g. "Projects" — builds the heading + button copy. */
    noun: string;
    /** dark = white type on the section's dark tiles; light = dark type. */
    theme: 'dark' | 'light';
    /** Box classes (background + aspect ratio), matching the sibling tiles. */
    boxClassName: string;
    /** Inline aspect-ratio for tiles that set it as a raw ratio, not a class. */
    boxStyle?: CSSProperties;
    /** Eyebrow label; mirrors the section eyebrows like "(01 – Projects)". */
    eyebrow?: string;
}

interface CarouselBrowseMoreTileProps extends BrowseMoreTileProps {
    /** Basis + padding classes, matching the sibling carousel tiles. */
    itemClassName: string;
}

/**
 * Closing CTA tile (BC-4516). Mirrors the sibling tiles' footprint (aspect box)
 * and the page's editorial header language — eyebrow, display heading, rounded
 * pill. Type and spacing are sized in container units (`cqw`) so the same stack
 * balances in both the wide Projects tile and the narrow Professionals tile,
 * with `max()` floors keeping it legible on small mobile tiles. Used standalone
 * in a grid cell, or wrapped in a `CarouselItem` via `CarouselBrowseMoreTile`.
 */
export const BrowseMoreTile = ({
    to,
    noun,
    theme,
    boxClassName,
    boxStyle,
    eyebrow = '(More)',
}: BrowseMoreTileProps) => {
    const isDark = theme === 'dark';
    return (
        <RoccoLink to={to} className="group block w-full">
            <div
                className={cn(
                    '[container-type:inline-size] relative w-full overflow-hidden border transition-colors',
                    isDark
                        ? 'border-white/40 group-hover:border-white/70'
                        : 'border-black/20 group-hover:border-black/40',
                    boxClassName,
                )}
                style={boxStyle}
            >
                {/* Padding + type live on an inner layer so their `cqw`
                    units resolve against this tile (the query container),
                    not the viewport. Centred so the stack balances in both
                    the wide Projects tile and the tall Professionals tile. */}
                <div className="absolute inset-0 flex flex-col justify-center gap-[6cqw] p-[7cqw]">
                    <div className="flex flex-col gap-[3cqw]">
                        <span
                            className={cn(
                                'font-sans text-[max(11px,2.4cqw)] leading-[1.07] font-normal tracking-[-0.044em]',
                                isDark ? 'text-white/70' : 'text-[#181818]/60',
                            )}
                        >
                            {eyebrow}
                        </span>
                        <h3
                            className={cn(
                                'm-0 font-sans text-[max(14px,8cqw)] leading-[1.02] font-normal tracking-[-0.02em]',
                                isDark ? 'text-white' : 'text-[#1f1f21]',
                            )}
                        >
                            Browse more
                            <br />
                            {noun}
                        </h3>
                    </div>
                    {/* The whole tile is the link (the wrapping RoccoLink),
                        so the pill is decorative. Hidden below `md` where the
                        tile is too small to fit it without clipping. */}
                    <span
                        className={cn(
                            'hidden w-fit items-center gap-[max(5px,0.9cqw)] rounded-full px-[max(13px,2.3cqw)] py-[max(7px,1.1cqw)] font-sans text-[max(12px,1.8cqw)] font-normal tracking-[-0.03em] whitespace-nowrap shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)] transition-opacity group-hover:opacity-90 md:inline-flex',
                            isDark
                                ? 'bg-white text-black'
                                : 'bg-[#181818] text-white',
                        )}
                    >
                        View all {noun}
                        <ArrowRight
                            className={cn(
                                'size-[max(11px,1.4cqw)] transition-transform group-hover:translate-x-1',
                                isDark
                                    ? 'fill-black text-black'
                                    : 'fill-white text-white',
                            )}
                            aria-hidden="true"
                        />
                    </span>
                </div>
            </div>
        </RoccoLink>
    );
};

/** Closing CTA slide appended after the last carousel tile (BC-4516). */
export const CarouselBrowseMoreTile = ({
    itemClassName,
    ...tileProps
}: CarouselBrowseMoreTileProps) => (
    <CarouselItem className={itemClassName}>
        <BrowseMoreTile {...tileProps} />
    </CarouselItem>
);
